import { getNestedOrFloatingElementById, getSexTranslatedOrOrig, getMostRecentFieldValue } from '@fs/zion-sls'
import uniqBy from 'lodash.uniqby'

/**
 * Returns the value of the active field info from the useSlsHighlights hook.
 *
 * @param {Object} field - The field object to extract the value from, usually the activeFieldInfo from useSlsHighlightsContext.
 * @returns {String|null} - Return the value of the field in string or null.
 */
export const getFieldValueText = (field) => {
  const { fieldType, value, fieldValues } = field || {}

  // Prioritize values coming from the form
  if (value) {
    if (value === fieldType && fieldValues?.length > 0 && fieldValues[0].origValue?.text) {
      return fieldValues[0].origValue.text
    }
    if (value?.primaryText) {
      return value.primaryText
    }
    if (value?.standard?.standardizedText) {
      return value.standard.standardizedText
    }
    if (value?.originalText) {
      return value.originalText
    }
    if (value?.value) {
      return value.value
    }
    if (value !== fieldType && typeof value === 'string') {
      return value
    }
  }

  if (fieldType === 'SEX_CODE') {
    const sexValue = getSexTranslatedOrOrig(field)
    return sexValue.primaryText || sexValue
  }

  if (fieldType === 'EVENT_TYPE') {
    return field?.label
  }

  // If everything else fails, attempt to retrieve the most recent value from the element fieldValues
  return getMostRecentFieldValue(field) || ''
}

/**
 * Checks if any of the selected highlights are shared between multiple names.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Array} params.mainSelectedHighlights - The main highlights.
 * @param {Array} params.splitScreenSelectedHighlights - The split screen highlights.
 * @param {Object} params.types - Array of zion SLS types.
 * @param {string} params.fieldType - The type of the field.
 * @param {string} params.personId - The ID of the selected person.
 * @returns {boolean} - Returns true if any highlight is shared and either the field or element is shareable.
 */
export const checkIfSharedHighlights = ({
  mainSelectedHighlights,
  splitScreenSelectedHighlights,
  types,
  fieldType,
  personId,
}) => {
  const attachedHighlights = [...mainSelectedHighlights, ...splitScreenSelectedHighlights]
  const typeOfField = types.fields.find((type) => type.fieldType === fieldType)
  const fieldIsShareable = !typeOfField?.singleSuper

  const elementIsShareable = !types.elements.find((type) => type.elementType === typeOfField.elementTypes[0])
    ?.singleSuper

  const anyHighlightIsTaken = attachedHighlights?.find((highlight) => {
    // Filtering empty, null or undefined personIds
    const originalPersonIds = highlight.originalHighlight?.personIds.filter(Boolean)

    return originalPersonIds?.length > 0 && !originalPersonIds?.includes(personId)
  })

  return !!anyHighlightIsTaken && (fieldIsShareable || elementIsShareable)
}

/**
 * Extracts fields from shared highlights.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Array} params.highlights - The array of highlights.
 * @param {Array} params.images - Array of zion SLS images.
 * @param {Object} params.types - Array of zion SLS types.
 * @returns {Array} - Returns an array of fields associated with the shared highlights.
 */
export const getFieldsFromSharedHighlights = ({ highlights, images, types }) => {
  const fieldsOnHighlights = highlights.reduce((acc, highlight) => {
    if (highlight?.originalHighlight?.personIds?.length > 0) {
      // Get the shareable-level element for the field associated to the highlight
      let fieldOnHighlight = getNestedOrFloatingElementById(images, highlight?.originalHighlight?.fieldId)
      // Since highlights are on fields, we are at most only going to need to go up one level to find a shared field
      if (fieldOnHighlight?.typeData?.singleSuper)
        fieldOnHighlight = getNestedOrFloatingElementById(images, fieldOnHighlight?.superElements[0]?.id)
      acc.push(
        fieldOnHighlight || {
          id: highlight?.originalHighlight?.fieldId,
          elementType: 'FIELD',
          fieldType: highlight?.originalHighlights?.fieldType,
          label: types?.fields.find((type) => type.fieldType === highlight?.originalHighlight?.fieldType).displayLabel,
        }
      )
    }
    return acc
  }, [])

  return uniqBy(fieldsOnHighlights, (f) => f.id)
}
